/**
 * Warstwa API modułu: J — oferty pracy i rekrutacja.
 * Właściciel: FALA 1, agent J-1 (J-2/J-3 dopisują swoje) (docs/PROJEKT-00-FUNDAMENT-2026-09-02.md §5, §6).
 * Trasy backendu: api/job-offers.
 *
 * Kontrakt: `attach(api)` dostaje instancję axios z apiService.js (interceptory JWT, cache GET, obsługa 401)
 * i zwraca OBIEKT nazwanych metod, które apiService.js doczepia do wspólnego `apiService` (jedno miejsce,
 * src/api/modules/index.js). Nazwy metod muszą być unikalne w całej aplikacji — kolizja zatrzymuje start
 * (patrz attachApiModules). Tego pliku nie dotyka nikt poza właścicielem modułu.
 */
const NAGLOWEK_MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

export function attach(api) {
    /**
     * Zapis ogłoszenia idzie multipartem, bo w tym samym żądaniu jedzie okładka. Pola opisowe pakujemy
     * do JEDNEGO pola `data` (wzorzec konkursów) — inaczej każde nowe pole formularza wymagałoby zmiany
     * po obu stronach, a liczby i daty gubiłyby typ po drodze przez FormData.
     */
    const zbudujFormularz = (dto, coverFile) => {
        const fd = new FormData();
        fd.append("data", JSON.stringify(dto));
        if (coverFile) fd.append("coverImage", coverFile);
        return fd;
    };

    /**
     * Zgłoszenie kandydackie (aplikacja / polecenie): pola opisowe w jednym polu `data`, pliki osobno.
     * Ten sam wzorzec co przy ogłoszeniu — dzięki temu dodanie pola formularza nie wymaga zmian
     * po obu stronach, a liczby i daty nie gubią typu w FormData.
     */
    const zbudujZgloszenie = (dto, files) => {
        const fd = new FormData();
        fd.append("data", JSON.stringify(dto));
        (files || []).forEach((plik) => fd.append("files", plik));
        return fd;
    };

    return {
        // ── Pracownik ───────────────────────────────────────────────────────────────────────────
        getJobOffers: (params = {}) => api.get("/job-offers", { params }),
        getJobOfferBySlug: (slug) => api.get(`/job-offers/slug/${encodeURIComponent(slug)}`),
        registerJobOfferView: (id, secondsOnPage) =>
            api.post(`/job-offers/${id}/view`, { secondsOnPage }),

        // ── Redakcja (PERM:JobOffersManage) ─────────────────────────────────────────────────────
        getJobOffersAdmin: (params = {}) => api.get("/job-offers/admin", { params }),
        getJobOfferAdmin: (id) => api.get(`/job-offers/admin/${id}`),
        getJobOfferEditorOptions: () => api.get("/job-offers/admin/options"),
        checkJobOfferSlug: (slug, exceptId) =>
            api.get("/job-offers/admin/check-slug", { params: { slug, exceptId } }),
        createJobOffer: (dto, coverFile) =>
            api.post("/job-offers", zbudujFormularz(dto, coverFile), NAGLOWEK_MULTIPART),
        updateJobOffer: (id, dto, coverFile) =>
            api.put(`/job-offers/${id}`, zbudujFormularz(dto, coverFile), NAGLOWEK_MULTIPART),
        changeJobOfferStatus: (id, status, reason) =>
            api.patch(`/job-offers/${id}/status`, { status, reason }),
        deleteJobOffer: (id) => api.delete(`/job-offers/${id}`),
        getJobRecruitmentSettings: () => api.get("/job-offers/admin/settings"),
        saveJobRecruitmentSettings: (consentText, referralConsentText, candidateRetentionMonths) =>
            api.put("/job-offers/admin/settings", { consentText, referralConsentText, candidateRetentionMonths }),

        // ── Załączniki ogłoszenia (etap 4) ──────────────────────────────────────────────────────
        /**
         * Dokłada pliki do ogłoszenia. Multipart z polami `files` (wiele) i opcjonalnym `title`
         * (etykieta ma sens tylko przy JEDNYM pliku — przy wielu naraz nazwałaby wszystkie tak samo).
         */
        addJobOfferAttachments: (offerId, files, title, onUploadProgress) => {
            const fd = new FormData();
            (files || []).forEach((plik) => fd.append("files", plik));
            if (title) fd.append("title", title);
            return api.post(`/job-offers/${offerId}/attachments`, fd, {
                ...NAGLOWEK_MULTIPART,
                onUploadProgress,
            });
        },
        deleteJobOfferAttachment: (attachmentId) => api.delete(`/job-offers/attachments/${attachmentId}`),

        /**
         * Treść załącznika jako blob — do okna PodgladDokumentu. Adres pliku na dysku NIE ISTNIEJE po
         * stronie przeglądarki: bajty wydaje endpoint, który sprawdza zasięg ogłoszenia i zapisuje pobranie.
         */
        getJobOfferAttachment: (attachmentId) =>
            api.get(`/job-offers/attachments/${attachmentId}`, { responseType: "blob" }),

        // ── Kandydat: aplikowanie i polecenia (etapy 2–3) ───────────────────────────────────────
        /**
         * Kontekst działania na ogłoszeniu: czy wolno aplikować/polecać (i DLACZEGO nie), treść klauzul,
         * warunki pakietu, moja aplikacja i moje polecenia. Jedno żądanie zamiast czterech — i jedno
         * miejsce, z którego bierze się komunikat przy nieaktywnym przycisku.
         */
        getJobApplyContext: (offerId) => api.get(`/job-offers/${offerId}/apply-context`),
        applyToJobOffer: (offerId, dto, files, onUploadProgress) =>
            api.post(`/job-offers/${offerId}/apply`, zbudujZgloszenie(dto, files), {
                ...NAGLOWEK_MULTIPART,
                onUploadProgress,
            }),
        withdrawJobApplication: (offerId) => api.delete(`/job-offers/${offerId}/my-application`),
        createJobReferral: (offerId, dto, files, onUploadProgress) =>
            api.post(`/job-offers/${offerId}/referrals`, zbudujZgloszenie(dto, files), {
                ...NAGLOWEK_MULTIPART,
                onUploadProgress,
            }),
        getMyJobApplications: () => api.get("/job-offers/my/applications"),
        getMyJobReferrals: () => api.get("/job-offers/my/referrals"),

        /**
         * Plik kandydata jako blob — do okna PodgladDokumentu. Adres pliku na dysku NIE ISTNIEJE po stronie
         * przeglądarki: treść przychodzi z endpointu, który sprawdza prawo i zapisuje wgląd w dzienniku.
         */
        getJobCandidateFile: (fileId) =>
            api.get(`/job-offers/candidate-files/${fileId}`, { responseType: "blob" }),

        // ── Kadry (JobCandidatesView / JobCandidatesManage) ─────────────────────────────────────
        getJobApplications: (params = {}) => api.get("/job-offers/applications", { params }),
        getJobApplication: (id) => api.get(`/job-offers/applications/${id}`),
        getJobReferrals: (params = {}) => api.get("/job-offers/referrals", { params }),
        getJobReferral: (id) => api.get(`/job-offers/referrals/${id}`),
        getJobRecruitmentSummary: () => api.get("/job-offers/recruitment-summary"),
        changeJobApplicationStatus: (id, status, note) =>
            api.patch(`/job-offers/applications/${id}/status`, { status, note }),
        changeJobReferralStatus: (id, status, note) =>
            api.patch(`/job-offers/referrals/${id}/status`, { status, note }),
        markJobReferralHired: (id, hiredAt, hiredUserId) =>
            api.post(`/job-offers/referrals/${id}/hired`, { hiredAt, hiredUserId }),
        issueJobReferralBonus: (id) => api.post(`/job-offers/referrals/${id}/bonus-request`),
        getJobApplicationHirePrefill: (id) => api.get(`/job-offers/applications/${id}/hire-prefill`),
        getJobReferralHirePrefill: (id) => api.get(`/job-offers/referrals/${id}/hire-prefill`),
        purgeJobCandidateFile: (fileId) => api.delete(`/job-offers/candidate-files/${fileId}`),

        // ── Pakiety premii (redakcja ogłoszeń) ──────────────────────────────────────────────────
        getJobReferralPackages: (onlyActive = false) =>
            api.get("/job-offers/admin/referral-packages", { params: { onlyActive } }),
        createJobReferralPackage: (dto) => api.post("/job-offers/admin/referral-packages", dto),
        updateJobReferralPackage: (id, dto) => api.put(`/job-offers/admin/referral-packages/${id}`, dto),
    };
}
